import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, TextInput, View } from 'react-native';

import { CitySelectorSheet } from '@/components/discovery/city-selector-sheet';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useDiscoveryCategories } from '@/hooks/use-discovery';
import { useUpdateMyPublicProfile } from '@/hooks/use-public-profile';
import { getApiErrorMessage } from '@/utils/api-error';
import { useToastStore } from '@/store/toastStore';
import { PublicProfileConfig } from '@/types/public-profile';
import { cn } from '@/utils/cn';
import { useThemedHex } from '@/hooks/use-theme-colors';

type DiscoveryLocationCardProps = {
  profile: PublicProfileConfig;
};

/**
 * Captura opcional de região e categoria (A1/A2 da story de localização).
 * Sem cidade, o perfil continua acessível pelo link — só não aparece nas
 * buscas por região. O incentivo é o alcance, nunca a obrigação.
 */
export function DiscoveryLocationCard({ profile }: DiscoveryLocationCardProps) {
  const th = useThemedHex();
  const { addToast } = useToastStore();
  const updateProfile = useUpdateMyPublicProfile();
  const { data: categories } = useDiscoveryCategories();

  const [categorySlug, setCategorySlug] = useState<string | null>(profile.categorySlug);
  const [neighborhood, setNeighborhood] = useState(profile.neighborhood ?? '');
  const [cityPickerOpen, setCityPickerOpen] = useState(false);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    setCategorySlug(profile.categorySlug);
    setNeighborhood(profile.neighborhood ?? '');
  }, [profile]);

  const locationLabel = profile.city
    ? `${profile.city} · ${profile.state}`
    : 'Escolher cidade';

  const handleSelectCity = (region: { uf: string | null; city: string | null }) => {
    updateProfile.mutate(
      { state: region.uf, city: region.city },
      {
        onSuccess: () => addToast('Localização atualizada!', 'success'),
        onError: (error: any) =>
          addToast(getApiErrorMessage(error, 'Erro ao salvar localização.'), 'error'),
      },
    );
  };

  const handleSave = () => {
    updateProfile.mutate(
      {
        categorySlug,
        neighborhood: neighborhood.trim() || null,
      },
      {
        onSuccess: () => addToast('Informações salvas!', 'success'),
        onError: (error: any) =>
          addToast(getApiErrorMessage(error, 'Erro ao salvar.'), 'error'),
      },
    );
  };

  return (
    <Card>
      <CardContent className="gap-4 py-4">
        <View className="gap-0.5">
          <ThemedText className="font-semibold text-foreground">Onde você atende</ThemedText>
          <ThemedText className="text-xs leading-4 text-neutral-500">
            Opcional. Preencher faz você aparecer para clientes que buscam perto de você.
          </ThemedText>
        </View>

        <View className="gap-2">
          <ThemedText className="text-sm font-medium">Cidade</ThemedText>

          <Pressable
            onPress={() => setCityPickerOpen(true)}
            className="h-11 flex-row items-center justify-between rounded-full bg-neutral-100 px-4"
          >
            <View className="flex-row items-center gap-2">
              <Ionicons className="text-neutral-400" name="location-outline" size={15} />
              <ThemedText
                className={cn('text-sm', profile.city ? 'text-foreground' : 'text-neutral-400')}
              >
                {locationLabel}
              </ThemedText>
            </View>

            <Ionicons className="text-neutral-400" name="chevron-down" size={15} />
          </Pressable>
        </View>

        <View className="gap-2">
          <ThemedText className="text-sm font-medium">Bairro</ThemedText>

          <TextInput
            value={neighborhood}
            onChangeText={setNeighborhood}
            placeholder="Ex: Batel"
            placeholderTextColor="#A3A3A3"
            style={Platform.OS === 'web' ? ({ outline: 'none' } as never) : undefined}
            className="h-11 rounded-full bg-neutral-100 px-4 text-sm text-foreground"
          />
        </View>

        <View className="gap-2">
          <ThemedText className="text-sm font-medium">Categoria</ThemedText>
          <ThemedText className="text-xs text-neutral-500">
            É por ela que os clientes filtram a busca.
          </ThemedText>

          <View className="flex-row flex-wrap gap-2 pt-1">
            {(categories ?? []).map((category) => {
              const isSelected = categorySlug === category.slug;

              return (
                <Pressable
                  key={category.slug}
                  onPress={() => setCategorySlug(isSelected ? null : category.slug)}
                  className={cn(
                    'flex-row items-center gap-1.5 rounded-full border px-3 py-1.5',
                    isSelected
                      ? 'border-primary bg-primary-50'
                      : 'border-neutral-900/5 bg-neutral-50',
                  )}
                >
                  {category.icon ? (
                    <Ionicons
                      name={category.icon as never}
                      size={13}
                      color={isSelected ? '#FD5001' : th('#737373')}
                    />
                  ) : null}

                  <ThemedText
                    className={cn(
                      'text-xs',
                      isSelected ? 'font-semibold text-primary' : 'text-neutral-600',
                    )}
                  >
                    {category.label}
                  </ThemedText>
                </Pressable>
              );
            })}
          </View>
        </View>

        <View className="flex-row items-center justify-between border-t border-neutral-100 pt-3">
          <View className="flex-1 gap-0.5 pr-3">
            <ThemedText className="text-sm font-medium">Aparecer na busca</ThemedText>
            <ThemedText className="text-xs leading-4 text-neutral-500">
              Desligado, seu perfil só é acessado por quem tem o link.
            </ThemedText>
          </View>

          <Switch
            checked={profile.isDiscoverable}
            onCheckedChange={(value) =>
              updateProfile.mutate(
                { isDiscoverable: value },
                { onError: () => addToast('Erro ao atualizar.', 'error') },
              )
            }
          />
        </View>

        <Button
          title={updateProfile.isPending ? 'Salvando...' : 'Salvar'}
          disabled={updateProfile.isPending}
          onPress={handleSave}
          className="w-full"
        />
      </CardContent>

      <CitySelectorSheet
        open={cityPickerOpen}
        region={{ uf: profile.state, city: profile.city }}
        onClose={() => setCityPickerOpen(false)}
        onSelect={handleSelectCity}
        allowNationwide={false}
      />
    </Card>
  );
}
